import * as React from "react";
import { Link } from "react-router-dom";
import { Search, Flame, LogOut, Settings, User, Trophy, Save, Loader2, ChevronDown, X, Upload, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PfpAvatar } from "@/components/pfp-avatar";
import { GooeyInput } from "@/components/aceternity/gooey-input";
import { useAuth } from "@/lib/auth-context";
import { updateProfile, fetchLeaderboard, uploadAvatar, AVATAR_ACCEPT } from "@/lib/queries";
import { cn } from "@/lib/utils";

type Props = { compact?: boolean; showSearch?: boolean };

export function AppTopBar({ compact = false, showSearch = true }: Props) {
  const { user, access, refreshAccess, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [streak, setStreak] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const board = await fetchLeaderboard(200);
        if (cancelled) return;
        setStreak(board.find(r => r.id === user?.id)?.streak ?? 0);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, access?.profile.total_points]);

  // Live points: refresh when modes fire arabise:points-updated.
  React.useEffect(() => {
    const onUpd = () => void refreshAccess();
    window.addEventListener("arabise:points-updated", onUpd);
    return () => window.removeEventListener("arabise:points-updated", onUpd);
  }, [refreshAccess]);

  const points = access?.profile.total_points ?? 0;
  const name   = access?.profile.full_name || user?.email?.split("@")[0] || "you";

  return (
    <>
      <header className={cn(
        "sticky top-0 z-40 bg-bg/85 backdrop-blur-xl border-b border-line",
        compact ? "py-2" : "py-3"
      )}>
        <div className="mx-auto max-w-7xl px-4 md:px-6 flex items-center gap-3 md:gap-4">
          <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0">
            <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
            <span className="font-bold tracking-[0.15em] text-sm hidden sm:inline">ARABISE</span>
          </Link>

          {showSearch && (
            <div className="flex-1 max-w-2xl hidden md:block">
              <SearchBox />
            </div>
          )}
          {!showSearch && <div className="flex-1" />}

          {/* Points pill */}
          <Link to="/leaderboard"
            id="arabise-points-pill"
            className="inline-flex items-center gap-1.5 rounded-full border border-accent-lo bg-accent/10 hover:bg-accent/15 transition-colors px-3 h-9 text-xs font-semibold text-accent">
            <Trophy className="w-3.5 h-3.5" />
            <span>{points.toLocaleString()}</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-widest opacity-80">pts</span>
          </Link>

          {/* Streak chip */}
          <Link to="/streak"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 h-9 text-xs font-semibold transition-colors",
              streak > 0
                ? "border-accent-lo bg-accent/10 hover:bg-accent/15 text-accent"
                : "border-line bg-bg-2 hover:bg-bg-3 text-fg-2"
            )}>
            <Flame className={cn("w-3.5 h-3.5", streak > 0 && "drop-shadow-[0_0_4px_currentColor]")} />
            <span>{streak}</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-widest opacity-80">
              {streak === 1 ? "day" : "days"}
            </span>
          </Link>

          {/* Profile menu */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              className="flex items-center gap-2 rounded-full pl-1 pr-2 h-9 border border-line bg-bg-2 hover:bg-bg-3 transition-colors"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <PfpAvatar size="sm" url={access?.profile.avatar_url} name={name} seed={user?.id} />
              <span className="hidden md:inline text-xs font-semibold max-w-[100px] truncate">{name}</span>
              <ChevronDown className="w-3.5 h-3.5 text-fg-3" />
            </button>

            {menuOpen && (
              <>
                {/* Click-outside backdrop */}
                <button
                  type="button" aria-hidden
                  onClick={() => setMenuOpen(false)}
                  className="fixed inset-0 z-40 cursor-default"
                />
                <div role="menu" className="absolute right-0 top-full mt-2 w-64 z-50 rounded-2xl border border-line bg-bg-2 shadow-soft p-1.5">
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <PfpAvatar size="md" url={access?.profile.avatar_url} name={name} seed={user?.id} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{name}</div>
                      <div className="text-xs text-fg-3 truncate">{user?.email}</div>
                    </div>
                  </div>
                  <Separator />
                  <MenuItem onClick={() => { setEditorOpen(true); setMenuOpen(false); }}>
                    <Settings className="w-4 h-4" /> Edit profile
                  </MenuItem>
                  <MenuLink to="/profile">
                    <User className="w-4 h-4" /> Full profile
                  </MenuLink>
                  <MenuLink to="/leaderboard">
                    <Trophy className="w-4 h-4" /> Leaderboard
                  </MenuLink>
                  <MenuLink to="/streak">
                    <Flame className="w-4 h-4" /> Your streak
                  </MenuLink>
                  <Separator />
                  <MenuItem onClick={() => signOut()}>
                    <LogOut className="w-4 h-4" /> Sign out
                  </MenuItem>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile search row */}
        {showSearch && (
          <div className="mt-2 mx-auto max-w-7xl px-4 md:hidden">
            <SearchBox />
          </div>
        )}
      </header>

      {editorOpen && <ProfileEditor onClose={() => setEditorOpen(false)} />}
    </>
  );
}

function SearchBox() {
  return (
    <GooeyInput
      type="search"
      placeholder="Search any word — try 'house', 'kitāb' or بَيت"
      left={<Search className="w-4 h-4" />}
      right={
        <kbd className="hidden sm:inline-flex items-center justify-center h-6 px-1.5 text-[11px] rounded border border-line bg-bg text-fg-3">/</kbd>
      }
    />
  );
}

function Separator() {
  return <div className="my-1 h-px bg-line" />;
}

function MenuItem({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button type="button" role="menuitem" onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-fg-2 hover:text-fg hover:bg-bg-3 transition-colors">
      {children}
    </button>
  );
}

function MenuLink({ children, to }: { children: React.ReactNode; to: string }) {
  return (
    <Link role="menuitem" to={to}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-fg-2 hover:text-fg hover:bg-bg-3 transition-colors">
      {children}
    </Link>
  );
}

/* ───────────────────────── Profile Editor Modal ───────────────────────── */

function ProfileEditor({ onClose }: { onClose: () => void }) {
  const { user, access, refreshAccess } = useAuth();
  const [name, setName]       = React.useState(access?.profile.full_name || "");
  const [avatar, setAvatar]   = React.useState(access?.profile.avatar_url || "");
  const [previewUrl, setPreview] = React.useState<string | null>(null);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [busy, setBusy]       = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [err,  setErr]        = React.useState("");
  const fileRef = React.useRef<HTMLInputElement>(null);

  // Clean up object URLs.
  React.useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  function onPickFile() { fileRef.current?.click(); }

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    setPendingFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreview(URL.createObjectURL(f));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      let nextAvatar = avatar.trim();
      if (pendingFile) {
        setUploading(true);
        nextAvatar = await uploadAvatar(pendingFile);
        setAvatar(nextAvatar);
        setUploading(false);
      }
      await updateProfile({
        full_name:  name.trim(),
        avatar_url: nextAvatar
      });
      await refreshAccess();
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Couldn't save.");
      setUploading(false);
    } finally {
      setBusy(false);
    }
  }

  const displayedUrl = previewUrl || avatar;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-bg/80 backdrop-blur-sm" role="dialog" aria-modal>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0" />
      <div className="relative w-full max-w-md rounded-3xl border border-line bg-bg-2 p-7 shadow-soft">
        <button type="button" onClick={onClose}
          className="absolute top-4 right-4 inline-flex items-center justify-center w-8 h-8 rounded-full text-fg-3 hover:text-fg hover:bg-bg-3 transition-colors">
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-xl font-bold tracking-tight">Edit profile</h2>
        <p className="text-sm text-fg-2 mt-1">How you appear on leaderboards.</p>

        <div className="mt-6 flex flex-col items-center gap-3">
          <PfpAvatar size="xl" url={displayedUrl} name={name} seed={user?.id} />
          <input
            ref={fileRef}
            type="file"
            accept={AVATAR_ACCEPT}
            className="hidden"
            onChange={onFileChosen}
          />
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onPickFile} disabled={busy}>
              <Upload className="w-3.5 h-3.5" /> Upload image
            </Button>
            {(previewUrl || avatar) && (
              <Button type="button" variant="ghost" size="sm" onClick={() => {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreview(null);
                setPendingFile(null);
                setAvatar("");
              }} disabled={busy}>
                <X className="w-3.5 h-3.5" /> Remove
              </Button>
            )}
          </div>
          <p className="text-[11px] text-fg-3 text-center">
            PNG, JPG, WEBP, GIF or AVIF · up to 5 MB
          </p>
        </div>

        <form className="mt-6 space-y-4" onSubmit={onSave}>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-fg-3 mb-2">Display name</span>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" maxLength={64} required />
          </label>

          {err && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-bad/40 bg-bad/10 px-3 py-2 text-sm text-bad">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" className="flex-1" disabled={busy || !name.trim()}>
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                : busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : <><Save className="w-4 h-4" /> Save</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
